// Palstro Sync Manager — flushes pending offline operations to Supabase

import { supabase } from './supabase'
import { getPendingOps, markSynced, isOfflineWriteAllowed } from './offlineDB'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

export type SyncResult = {
  synced: number
  failed: number
  errors: string[]
}

export async function syncPendingOps(): Promise<SyncResult> {
  const ops = await getPendingOps()
  let synced = 0
  let failed = 0
  const errors: string[] = []

  for (const op of ops) {
    if (op.id === undefined) continue
    if (!isOfflineWriteAllowed(op.table)) {
      // Defense-in-depth: queueOp() should already block this, but never replay
      // raw data against a non-allow-listed table. Flag (mark handled) and skip.
      console.warn(`[Sync] Blocked replay to non-allow-listed table: ${op.table}`)
      errors.push(`${op.table}:${op.operation} — blocked (table not in offline allow-list)`)
      failed++
      await markSynced(op.id)
      continue
    }
    try {
      if (op.operation === 'insert') {
        const { error } = await db(op.table).insert(op.payload)
        if (error) throw error
      } else if (op.operation === 'update') {
        const { id, ...patch } = op.payload
        const { error } = await db(op.table).update(patch).eq('id', id)
        if (error) throw error
      } else if (op.operation === 'delete') {
        const { error } = await db(op.table).delete().eq('id', op.payload.id)
        if (error) throw error
      }
      await markSynced(op.id)
      synced++
    } catch (e) {
      failed++
      errors.push(`${op.table}:${op.operation} — ${e instanceof Error ? e.message : String(e)}`)
    }
  }

  return { synced, failed, errors }
}

/** Register service worker and request background sync */
export async function registerSW(): Promise<void> {
  if (!('serviceWorker' in navigator)) return
  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' })
    console.log('[SW] Registered', reg.scope)

    // Listen for sync messages from SW
    navigator.serviceWorker.addEventListener('message', async (event) => {
      if (event.data?.type === 'SYNC_REQUESTED') {
        await syncPendingOps()
      }
    })
  } catch (e) {
    console.warn('[SW] Registration failed', e)
  }
}

/**
 * Remove any service worker previously registered on this origin and purge its
 * caches. Used to self-heal storefront / custom-domain origins where an older
 * app build wrongly installed the platform SW — that SW serves the app-shell
 * offline fallback, which breaks the public shop. Idempotent and a no-op when
 * nothing is registered.
 */
export async function unregisterSW(): Promise<void> {
  if (!('serviceWorker' in navigator)) return
  try {
    const regs = await navigator.serviceWorker.getRegistrations()
    if (regs.length === 0) return
    await Promise.all(regs.map(r => r.unregister()))
    if ('caches' in self) {
      const keys = await caches.keys()
      await Promise.all(keys.map(k => caches.delete(k)))
    }
    console.log('[SW] Unregistered on storefront origin')
  } catch (e) {
    console.warn('[SW] Unregister failed', e)
  }
}

/** Trigger sync when coming back online */
export function setupOnlineSync(): () => void {
  const handler = async () => {
    const result = await syncPendingOps()
    if (result.synced > 0) {
      // Dispatch a custom event so React components can react
      window.dispatchEvent(new CustomEvent('palstro-sync-complete', { detail: result }))
    }
  }
  window.addEventListener('online', handler)
  return () => window.removeEventListener('online', handler)
}
